import React, { Fragment } from 'react'
import { A } from '@modul-connect/shared/components/atoms/text'
import View from '../../atoms/view'
import { SidebarIcon } from '../../atoms/sideBarIcon'
import theme from '../../../theme'
import chroma from 'chroma-js'
import { useLocation, useNavigate } from 'react-router-dom'
import { CustomTooltip } from '../../molecules/tooltip'

const SideBarLinks = ({
  isSelected,
  sidebarContent,
  onClick,
  iconOnly,
  textOnly,
}) => {
  return (
    <View style={iconOnly && { borderBottom: `0.5px solid ${theme.colors.primaryDark}` }}>
      {sidebarContent.map((item, index) => {
        return item.hide ? null : (
          <SidebarLink
            key={index}
            iconOnly={iconOnly}
            textOnly={textOnly}
            title={item.title}
            id={item.id}
            icon={item.icon}
            onClick={() => { onClick(index) }}
            isSelected={isSelected(index)}
          />
        )
      })}
    </View>
  )
}

export const SidebarLink = ({
  title, id, icon, isSelected, onClick, iconOnly, textOnly
}) => {
  const location = useLocation()
  const navigate = useNavigate()

  return (
    <CustomTooltip title={title} placement="right-start">
      <View
        extend={[
          styles.linkRow,
          iconOnly && !textOnly && styles.link_iconOnly,
          textOnly && !iconOnly && styles.link_textOnly,
          isSelected ? styles.link_selected : styles.link_unselected,
        ]}
        onClick={() => onClick(id, location, navigate, [], location)}
      >
        {textOnly ? null : ( <LinkIcon icon={icon} isSelected={isSelected} rail={iconOnly && !textOnly} />)}
        {iconOnly ? null : (
          <LinkText
            isSelected={isSelected}
            onClick={onClick}
            text={title}
            extend={textOnly && styles.link_textOnly_text}
          />
        )}
      </View>
    </CustomTooltip>
  )
}

export const SidebarLinkForSubpages = ({
  id, title, icon, isSelected, onClick, iconOnly, textOnly, subPages, nodes
}) => {
  const location = useLocation()
  const navigate = useNavigate()

  let isSubPageSelected = true
  if (subPages?.length >= 2 || nodes?.length) {
    const parts = location.pathname.split('/')
    if (parts.length > 3) {
      isSubPageSelected = parts[3]?.toLowerCase() === id?.toLowerCase()
    } else {
      isSubPageSelected = false
    }
  }

  return (
    <View
      extend={[
        styles.linkRow,
        iconOnly && !textOnly && styles.link_iconOnly,
        textOnly && !iconOnly && styles.link_textOnly,
        isSubPageSelected ? styles.link_selected : styles.link_unselected,
      ]}
      onClick={() => onClick(id, location, navigate, subPages, location)}
    >
      {textOnly ? null : icon ? <LinkIcon icon={icon} /> : null}

      <LinkText
        isSelected={isSubPageSelected}
        onClick={onClick}
        text={title}
        extend={[textOnly && styles.link_textOnly_text]}
      />
    </View>
  )
}

const LinkIcon = ({ icon, isSelected, rail }) => {
  return (
    <View
      extend={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <SidebarIcon
        dark
        icon={icon}
        selected={isSelected}
        size={rail ? 22 : 18}
        large={rail}
      />
    </View>
  )
}

const LinkText = ({ isSelected, onClick, text, extend }) => {
  return (
    <Fragment>
      <A
        extend={[isSelected ? styles.text_selected : styles.text_unselected, extend]}
        onClick={onClick}
        text={text}
      />
    </Fragment>
  )
}

/* ───────────────────────── styles ───────────────────────── */
const styles = {
  linkRow: ({ theme: { colors } }) => ({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    width: '100%',
    minHeight: 27,
    borderRadius: 6,
    padding: '4px 12px',
    cursor: 'pointer',
    textDecoration: 'none',
    '> a': {
      marginTop: 0,
      marginBottom: 0,
      paddingLeft: 0,
    },
  }),

  link_iconOnly: ({ theme: { colors, layout } }) => ({
    height: layout.sidebarItemHeight,
    width: layout.sidebarItemWidth,
    minHeight: layout.sidebarItemHeight,
    borderTop: `1px solid ${colors.lightDividerGrey}`,
    borderLeft: `1px solid ${colors.lightDividerGrey}`,
    borderRadius: 0,
    padding: 0,
    justifyContent: 'center',
  }),

  /* legacy "textOnly" */
  link_textOnly: ({ theme: { layout } }) => ({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'flex-start',
    marginTop: 12,
    marginBottom: 12,
    height: layout.sidebarItemHeight / 2,
    width: '100%',
    borderRadius: 6,
    padding: '6px 12px',
  }),
  link_textOnly_text: () => ({
    fontFamily: 'Inter',
    fontStyle: 'normal',
    fontWeight: 400,
    fontSize: '16px',
    lineHeight: '20px',
    color: '#4D4D4D',
  }),

  link_selected: ({ theme: { colors } }) => ({
    backgroundColor: colors.primaryDark,
    border: `0.5px solid ${colors.primaryDark}`,
    '&:hover': {
      backgroundColor: chroma(colors.primaryDark).darken(0.2).hex(),
    },
    '& a': { color: colors.white },
    '& img': {
      filter: 'invert(100%) contrast(100%) brightness(200%)',
    },
  }),

  link_unselected: ({ theme: { colors } }) => ({
    backgroundColor: 'transparent',
    border: '0.5px solid transparent',
    '&:hover': {
      backgroundColor: chroma(colors.lightGrey).alpha(0.35).hex(),
    },
    '& a': { color: '#4D4D4D' },
  }),

  text_selected: ({ theme: { colors } }) => ({
    fontFamily: 'Inter',
    fontWeight: 400,
    fontSize: 16,
    lineHeight: '20px',
    color: colors.white,
  }),
  text_unselected: () => ({
    fontFamily: 'Inter',
    fontWeight: 400,
    fontSize: 16,
    lineHeight: '20px',
    color: '#4D4D4D',
  }),
}

export default SideBarLinks